import React, { useContext, useState } from 'react';
import { Button, Form, Message } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Turnstile from 'react-turnstile';
import { StatusContext } from '../context/Status';
import { API, getCachedStatus, showError, showInfo } from '../helpers';
import AuthLayout from '../layout/AuthLayout';

const PasswordResetForm = () => {
  const { t } = useTranslation();
  const [statusState] = useContext(StatusContext);
  const [email, setEmail] = useState('');
  const [sentEmail, setSentEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileKey, setTurnstileKey] = useState(0);
  const status = statusState?.status || getCachedStatus();
  const turnstileSiteKey = status.turnstile_site_key || '';
  const turnstileEnabled = Boolean(status.turnstile_check && turnstileSiteKey);

  const handleSubmit = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail || loading) return;
    if (turnstileEnabled && !turnstileToken) {
      showInfo(t('messages.error.turnstile_wait'));
      return;
    }

    setLoading(true);
    try {
      const res = await API.get('/api/reset_password', {
        params: { email: normalizedEmail, turnstile: turnstileToken },
      });
      if (!res?.data) return;
      const { success, message } = res.data;
      if (success) {
        setSentEmail(normalizedEmail);
      } else {
        showError(message);
        setTurnstileToken('');
        setTurnstileKey((current) => current + 1);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t('auth.reset.title')}
      description={t('auth.reset.subtitle')}
    >
      {sentEmail ? (
        <Message success role='status'>
          <Message.Header>{t('auth.reset.sent_title')}</Message.Header>
          <p>{t('auth.reset.sent_description', { email: sentEmail })}</p>
          <div className='fvv-auth-footer'>
            <Link to='/login'>{t('auth.reset.back_login')}</Link>
          </div>
        </Message>
      ) : (
        <>
          <Form size='large' className='fvv-form' onSubmit={handleSubmit}>
            <Form.Input
              fluid
              icon='mail outline'
              iconPosition='left'
              placeholder={t('auth.reset.email')}
              name='email'
              type='email'
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete='email'
              autoFocus
            />
            {turnstileEnabled && (
              <div style={{ marginBottom: 16 }}>
                <Turnstile
                  key={turnstileKey}
                  sitekey={turnstileSiteKey}
                  size='flexible'
                  fixedSize
                  refreshExpired='auto'
                  onVerify={setTurnstileToken}
                  onExpire={() => setTurnstileToken('')}
                  onError={() => setTurnstileToken('')}
                />
              </div>
            )}
            <Button
              fluid
              primary
              type='submit'
              loading={loading}
              disabled={loading || !email.trim()}
            >
              {loading ? t('auth.reset.sending') : t('auth.reset.button')}
            </Button>
          </Form>
          <p className='fvv-auth-footer'>{t('auth.reset.notice')}</p>
          <div className='fvv-auth-footer'>
            <Link to='/login'>{t('auth.reset.back_login')}</Link>
          </div>
        </>
      )}
    </AuthLayout>
  );
};

export default PasswordResetForm;
